import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["especie", "field", "fieldNac", "fieldAima", "acaoTipoCombobox", "faseCombobox", "bancoSection", "nacionalidadeSection", "aimaSection", "outrosSection", "previdenciaSection"]

  connect() {
    this.destacarCampos()
    this.toggleSections()
  }

  acaoChanged(event) {
    this._currentAcao = event?.detail?.value ?? this.acaoValue
    this.preencherEspecie()
    this.carregarAcaoTipos()
    this.toggleSections()
    this.destacarCampos()
  }

  acaoTipoChanged() {
    this.carregarFasesEHonorario()
    this.destacarCampos()
  }

  // ─── leitura de valores ─────────────────────────────────────────────────────

  get acaoValue() {
    if (this._currentAcao !== undefined) return this._currentAcao
    return this.element.querySelector('input[name="processo[acao]"][data-ui--combobox-target="hidden"]')?.value ||
           this.element.dataset.processoFormAcao ||
           ""
  }

  get acaoTipoIdValue() {
    return this.element.querySelector('input[name="processo[acao_tipo_id]"][data-ui--combobox-target="hidden"]')?.value || ""
  }

  get acaoTipoLabelValue() {
    if (!this.hasAcaoTipoComboboxTarget) return ""
    // Lê direto do DOM para funcionar no connect() antes do combobox inicializar
    return this.acaoTipoComboboxTarget.querySelector('[data-ui--combobox-target="input"]')?.value?.trim() || ""
  }

  get honorarioEl() {
    return this.element.querySelector('textarea[name="processo[honorario]"]')
  }

  get extrajudicialEl() {
    return this.element.querySelector('input[type="checkbox"][name="processo[extrajudicial]"]')
  }

  // ─── cascade: acao → acao_tipo ───────────────────────────────────────────────

  async carregarAcaoTipos() {
    if (!this.hasAcaoTipoComboboxTarget) return
    const combobox = this.#comboboxController(this.acaoTipoComboboxTarget)
    if (!combobox) return

    this.#limparCombobox(combobox)
    const acao = this.acaoValue
    if (!acao) { combobox.inputTarget.disabled = true; return }

    combobox.inputTarget.disabled = false
    try {
      const url = `/filtro_acoes_tipos_por_acao?selected_acao=${encodeURIComponent(acao)}`
      const resp = await fetch(url, { headers: { Accept: "application/json" } })
      const options = await resp.json()
      this.#preencherCombobox(combobox, options)
      combobox.open()
      combobox.inputTarget.focus()
    } catch (e) { console.error("Erro ao carregar ação tipo:", e) }
  }

  // ─── cascade: acao_tipo → fase + honorario ────────────────────────────────

  async carregarFasesEHonorario() {
    if (!this.hasFaseComboboxTarget) return
    const combobox = this.#comboboxController(this.faseComboboxTarget)
    if (!combobox) return

    this.#limparCombobox(combobox)
    const acaoTipoId = this.acaoTipoIdValue
    if (!acaoTipoId) { combobox.inputTarget.disabled = true; return }

    try {
      const url = `/get_honorario_por_tipo_acao_e_fases?selected_acao_tipo_id=${encodeURIComponent(acaoTipoId)}`
      const resp = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await resp.json()

      if (data.honorario && this.honorarioEl && !this.honorarioEl.value) {
        this.honorarioEl.value = data.honorario
      }

      if (data.extrajudicial !== null && data.extrajudicial !== undefined && this.extrajudicialEl) {
        this.extrajudicialEl.checked = data.extrajudicial
      }

      combobox.inputTarget.disabled = false
      this.#preencherCombobox(combobox, data.fases || [])
    } catch (e) { console.error("Erro ao carregar fases:", e) }
  }

  // ─── lógica de formulário ────────────────────────────────────────────────────

  toggleSections() {
    const acao = this.acaoValue
    const isNacOuAima = ["Nacionalidade", "Ação Judicial AIMA"].includes(acao)
    if (this.hasPrevidenciaSectionTarget)
      this.previdenciaSectionTarget.classList.toggle("hidden", acao !== "Previdenciária")
    if (this.hasOutrosSectionTarget)
      this.outrosSectionTarget.classList.toggle("hidden", isNacOuAima)
    if (this.hasBancoSectionTarget)
      this.bancoSectionTarget.classList.toggle("hidden", acao !== "Bancária")
    if (this.hasNacionalidadeSectionTarget)
      this.nacionalidadeSectionTarget.classList.toggle("hidden", acao !== "Nacionalidade")
    if (this.hasAimaSectionTarget)
      this.aimaSectionTarget.classList.toggle("hidden", acao !== "Ação Judicial AIMA")
  }

  destacarCampos() {
    const acao = this.acaoValue
    const label = this.acaoTipoLabelValue
    const isNacOuAima = ["Nacionalidade", "Ação Judicial AIMA"].includes(acao)

    // field targets: visíveis apenas fora de Nacionalidade/AIMA; destaque para Previdenciária
    const DESTAQUES = {
      "Aposentadoria por Contribuição com Especial":                    ["vara", "data_der", "periodo_inss", "valor_causa"],
      "Aposentadoria por Contribuição com Rural":                       ["vara", "data_der", "periodo_inss", "valor_causa"],
      "Aposentadoria por Contribuição com Especial e Rural":            ["vara", "data_der", "periodo_inss", "valor_causa", "numero_beneficio"],
      "Aposentadoria por Idade Rural":                                  ["vara", "data_der", "valor_causa"],
      "Aposentadoria por Idade Rural - Bóia Fria":                      ["vara", "data_der", "valor_causa"],
      "Aposentadoria por Idade Híbrida":                                ["vara", "data_der", "valor_causa", "numero_beneficio", "data_indeferimento", "cid", "especialidade_medica"],
      "Auxílio Doença por Incapacidade Temporária Rural":               ["vara", "data_der", "valor_causa", "numero_beneficio", "data_indeferimento", "cid", "especialidade_medica"],
      "Auxílio Doença por Incapacidade Temporária Urbano":              ["vara", "data_der", "valor_causa", "numero_beneficio", "data_indeferimento", "cid", "especialidade_medica"],
    }
    const campos = DESTAQUES[label] || []
    this.fieldTargets.forEach(el => {
      el.classList.toggle("campo-destaque", campos.includes(el.dataset.campo))
    })

    // fieldNac targets: destaque apenas para Nacionalidade (BASE_NAC sempre destacados)
    const BASE_NAC = ["nome_registro_civil", "localidade_nascimento", "pais_residiu", "data_nascimento_mae", "pais_mae", "data_nascimento_pai", "pais_pai"]
    const DESTAQUES_NAC = {
      "Art.º 3º n.º 3 - Aquisição (união de facto)": [...BASE_NAC, "nome_conjuge", "data_nascimento_conjuge", "pais_conjuge", "data_uniao"],
      "Art.º 3º n.º 1 - Aquisição (casamento)":      [...BASE_NAC, "nome_conjuge", "data_nascimento_conjuge", "pais_conjuge", "data_uniao"],
      "Art.º 6º n.º 1 - Naturalização":               BASE_NAC,
      "Art.º 1º C - Atribuição (nasc. estrangeiro)":  BASE_NAC,
      "Art.º 1º D - Atribuição (netos de portugueses)": [...BASE_NAC, "nome_avo_mulher", "data_nascimento_avo_mulher", "pais_avo_mulher", "nome_avo_homem", "data_nascimento_avo_homem", "pais_avo_homem"],
    }
    const camposNac = acao === "Nacionalidade" ? (DESTAQUES_NAC[label] || BASE_NAC) : []
    this.fieldNacTargets.forEach(el => {
      el.classList.toggle("campo-destaque", camposNac.includes(el.dataset.campo))
    })

    // fieldAima targets: destaque por tipo de AIMA
    const DESTAQUES_AIMA = {
      "Reagrupamento Familiar":                  ["conjuge", "filho"],
      "Ar pelos Estudos":                        ["tipo_curso", "instituicao_ensino", "data_inicio_curso"],
      "Expedição Autorização de Residência":     ["teve_notificacao", "agendamento_aima"],
      "Agendamento sem Visto":                   ["data_emissao_visto", "data_validade_visto"],
    }
    const camposAima = acao === "Ação Judicial AIMA" ? (DESTAQUES_AIMA[label] || []) : []
    this.fieldAimaTargets.forEach(el => {
      el.classList.toggle("campo-destaque", camposAima.includes(el.dataset.campo))
    })
  }

  preencherEspecie() {
    if (this.acaoValue === "Previdenciária" && this.especieTarget.value === "") {
      this.especieTarget.value = "Ação em desfavor ao Instituto Nacional do Seguro Social (INSS)."
    }
  }

  // ─── helpers privados ────────────────────────────────────────────────────────

  #comboboxController(element) {
    return this.application.getControllerForElementAndIdentifier(element, "ui--combobox")
  }

  #limparCombobox(combobox) {
    combobox.hiddenTarget.value = ""
    combobox.inputTarget.value = ""
    if (combobox.updateClearButton) combobox.updateClearButton()
    combobox.optionTargets.forEach(opt => opt.remove())
    combobox.allOptions = []
  }

  #preencherCombobox(combobox, options) {
    options.forEach(opt => {
      const li = this.#criarElementoOpcao(opt)
      combobox.listTarget.insertBefore(li, combobox.hasEmptyTarget ? combobox.emptyTarget : null)
      combobox.allOptions.push({
        element: li,
        value: String(opt.value),
        label: (opt.label || "").toLowerCase(),
        description: ""
      })
    })
  }

  #criarElementoOpcao(opt) {
    const li = document.createElement("li")
    li.setAttribute("role", "option")
    li.setAttribute("aria-selected", "false")
    li.setAttribute("data-ui--combobox-target", "option")
    li.setAttribute("data-value", opt.value)
    li.setAttribute("data-label", opt.label)
    li.setAttribute("data-action", "click->ui--combobox#select mouseenter->ui--combobox#highlight")
    li.className = "relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-[#17a2b8] hover:text-white group"

    const wrapper = document.createElement("div")
    wrapper.className = "flex"
    const span = document.createElement("span")
    span.className = "truncate"
    span.textContent = opt.label
    wrapper.appendChild(span)
    li.appendChild(wrapper)
    return li
  }
}
